import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { getQuotations, Quotation } from '../services/QuotationsService';
import { ITableData } from '../types/interfaces';
import { routes } from '../routes/routePaths';

const Quotations = () => {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [filteredQuotations, setFilteredQuotations] = useState<Quotation[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const pageSize = 10;

  useEffect(() => {
    fetchQuotations();
  }, []);

  useEffect(() => {
    // Update pagination when data changes
    const start = page * pageSize;
    const end = start + pageSize;
    const paginatedData = quotations.slice(start, end);
    setFilteredQuotations(paginatedData);
    
    setTableData({
      size: pageSize,
      page: page,
      totalElements: quotations.length,
      totalPages: Math.ceil(quotations.length / pageSize),
    });
  }, [quotations, page]);

  const fetchQuotations = async () => {
    setIsLoading(true);
    try {
      const response = await getQuotations();
      if (response?.data) {
        setQuotations(response.data);
      } else {
        setQuotations([]);
      }
    } catch (error) {
      console.error('Error fetching quotations:', error);
      setQuotations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (newPage: number) => {
    if (newPage < 0 || newPage >= (tableData?.totalPages || 0)) {
      return;
    }
    setPage(newPage);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleRowClick = (row: Quotation) => {
    navigate(routes.quotationsDetail.replace(':assetCode', row.code), {
      state: { currency: row.name, assetId: row.id }
    });
  };

  const columns = [
    {
      name: 'Nombre',
      selector: (row: Quotation) => (
        <P type='4' variant='neutral'>
          {row.name}
        </P>
      ),
      minWidth: '150px',
      grow: 1,
    },
    {
      name: 'Última actualización',
      selector: (row: Quotation) => (
        <P type='4' variant='neutral'>
          {row.updatedAt}
        </P>
      ),
      minWidth: '150px',
      grow: 1,
    },
    {
      name: 'Valor venta',
      selector: (row: Quotation) => (
        <P type='4' variant='neutral'>
          {row.saleValue}
        </P>
      ),
      minWidth: '120px',
      grow: 0.8,
    },
    {
      name: 'Cotizaciones',
      selector: (row: Quotation) => (
        <Icon 
              name='view_outline'
              variant='info'
              modifier='regular'
              width={24}
              height={24}
              cursor='pointer'
              onClick={() => handleRowClick(row)}
            />
      ),
      minWidth: '120px',
      grow: 0.6,
    },
  ];

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' alignItems='center' gap='16px'>
        <Button variant='tertiary' onClick={handleGoBack}>
          <Icon name='arrow_left_outline' variant='tertiary' width={24} height={24} />
        </Button>
        <H withMargin={false} type='1' weight='bold' modifier='strong'>
          Activos a cotizar
        </H>
      </Card>

      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : filteredQuotations.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={filteredQuotations}
            tableData={tableData}
            handleChangePage={(e: any) => handleChangePage(e)}
            onRowClicked={(row: Quotation) => handleRowClick(row)}
          />
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay cotizaciones disponibles.
          </P>
        </Card>
      )}
    </Card>
  );
};

export default Quotations;
